/**
 * Canonical passenger ordering, established ONCE at the offer→checkout boundary.
 *
 * Per FRAMEWORK-SPEC §6 the within-room order is: all ≥18 hotel-adults first, then
 * everyone under 18 (infants, children, under-18 hotel-adults) by actual age ascending
 * (youngest first). Checkout then trusts the URL order and never re-sorts.
 *
 * Both functions are pure — they return new objects and never mutate their inputs.
 * See claude/specs/ChildVsInfant/IMPLEMENTATION-SPEC.md §3.
 */

import type { RoomOccupancy, Room, Person } from '@/types/SearchBar';
import type { PassengerInfo } from '@/types/schemas/player/common';
import type { RoomCombination, MappedOffer } from '@/services/mappers/offerMapper';

/**
 * Copy `hotelClass` from a flat `PassengerInfo[]` (carrying the hotel's PassengerCode)
 * back onto each `Person`, matched by room-then-person sequential index.
 *
 * `offerStore.roomOccupancy` is plain (no classification); the classification lives on
 * the mapped offer's passengers. Both enumerate passengers in the same id order
 * (room-then-person, ids 1..N), so index `k` in the flattened occupancy aligns with
 * `allPassengers[k]`.
 *
 * Returns a new occupancy; a passenger with no matching classification keeps
 * `hotelClass` undefined (binary fallback downstream).
 */
export function applyClassificationsToOccupancy(
  occ: RoomOccupancy,
  allPassengers: PassengerInfo[]
): RoomOccupancy {
  let index = 0;

  const rooms: Room[] = occ.rooms.map((room) => ({
    persons: room.persons.map((person) => {
      const hotelClass = allPassengers[index]?.hotelClass;
      index += 1;
      return hotelClass ? { ...person, hotelClass } : { ...person };
    }),
  }));

  return { rooms };
}

/**
 * A "senior" is a ≥18 hotel-adult — the people who sort first in a room.
 *
 * Binary adults (`type: 'adult'`, i.e. age ≥ 18 / the 35 sentinel) count as seniors
 * unless the hotel explicitly downgraded them to child/infant. Under-18 hotel-adults
 * are `type: 'child'` (age < 18) so they are NOT seniors — they sort with the juniors
 * by actual age.
 */
function isSeniorPerson(person: Person): boolean {
  if (person.type !== 'adult') return false;
  return person.hotelClass === undefined || person.hotelClass === 'adult';
}

/**
 * Map each product onto its searched room via the product's ORIGINAL Player passenger
 * ids: room k covers the contiguous id range [1 + Σ sizes of rooms 0..k-1, +size_k).
 * Returns the products reordered so index i corresponds to `rooms[i]`, or null when the
 * mapping cannot be established (empty passengerIds — the Player omits them for
 * single-room responses —, count mismatch, or two products claiming the same room).
 */
function alignProductsToRooms(products: MappedOffer[], rooms: Room[]): MappedOffer[] | null {
  if (products.length !== rooms.length) return null;

  const roomStartIds: number[] = [];
  let cursor = 1;
  for (const room of rooms) {
    roomStartIds.push(cursor);
    cursor += room.persons.length;
  }

  const aligned: (MappedOffer | undefined)[] = new Array(products.length);
  for (const product of products) {
    if (product.passengerIds.length === 0) return null;
    // Math.min because ids within a room may arrive shuffled (e.g. 1,2,4,3).
    const minId = Math.min(...product.passengerIds);
    const roomIndex = roomStartIds.findIndex(
      (start, i) => minId >= start && minId < start + rooms[i].persons.length
    );
    if (
      roomIndex < 0 ||
      aligned[roomIndex] ||
      product.passengerIds.length !== rooms[roomIndex].persons.length
    ) {
      return null;
    }
    aligned[roomIndex] = product;
  }

  return aligned as MappedOffer[];
}

/**
 * Reorder persons within each room to canonical order and recompute the combination's
 * passenger ids so each room stays a contiguous id range (required by
 * `createParticipantAssignmentForIds`, which throws on non-consecutive ids).
 *
 * `combination.products` are first aligned to `occ.rooms` via their original Player
 * passenger ids (the Player may return rooms in any order), falling back to index
 * alignment when the ids are unavailable — so `orderedCombination.products[i]` always
 * corresponds to `orderedOccupancy.rooms[i]`, the pairing `buildCheckoutUrl` relies on
 * when emitting offerIds/roomCodes per room.
 */
export function canonicalizeRoomOccupancy(
  occ: RoomOccupancy,
  combination: RoomCombination
): { orderedOccupancy: RoomOccupancy; orderedCombination: RoomCombination } {
  let nextId = 1;
  const orderedRooms: Room[] = [];
  const perRoomPassengers: PassengerInfo[][] = [];

  for (const room of occ.rooms) {
    const seniors = room.persons.filter(isSeniorPerson);
    const juniors = room.persons
      .filter((person) => !isSeniorPerson(person))
      // Youngest first; a missing age (a senior never reaches here) sorts as 0.
      .sort((a, b) => (a.age ?? 0) - (b.age ?? 0));

    const orderedPersons = [...seniors, ...juniors];
    orderedRooms.push({ persons: orderedPersons });

    // Assign new global sequential ids room-by-room → contiguous within each room.
    perRoomPassengers.push(
      orderedPersons.map((person) => ({
        id: nextId++,
        type: person.type,
        age: person.age,
        hotelClass: person.hotelClass,
      }))
    );
  }

  const orderedOccupancy: RoomOccupancy = { rooms: orderedRooms };

  if (combination.products.length !== occ.rooms.length) {
    console.warn(
      `[canonicalOrder] product/room count mismatch ` +
        `(${combination.products.length} products vs ${occ.rooms.length} rooms); ` +
        `passenger ids may not align.`
    );
  }

  const alignedProducts = alignProductsToRooms(combination.products, occ.rooms);
  if (!alignedProducts && combination.products.length > 1) {
    console.warn(
      `[canonicalOrder] could not align products to rooms via passenger ids; ` +
        `falling back to index alignment.`
    );
  }

  const orderedProducts: MappedOffer[] = (alignedProducts ?? combination.products).map(
    (product, i) => {
      const roomPassengers = perRoomPassengers[i];
      if (!roomPassengers) return product;
      return {
        ...product,
        passengerIds: roomPassengers.map((p) => p.id),
        passengers: roomPassengers,
      };
    }
  );

  const orderedCombination: RoomCombination = {
    ...combination,
    products: orderedProducts,
    allPassengers: perRoomPassengers.flat(),
  };

  return { orderedOccupancy, orderedCombination };
}
